import React, { useState } from 'react';
import api from '../api';

const S = { MANAGE: 'manage', ENTER: 'enter', CONFIRM: 'confirm' };

function PasswordInput({ label, value, onChange, placeholder, disabled }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="new-password"
          className="input-field pr-10"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors text-sm"
          tabIndex={-1}
        >
          {show ? '🙈' : '👁'}
        </button>
      </div>
    </div>
  );
}

function strength(pw) {
  if (!pw) return { level: 0, label: '', color: '' };
  let score = 0;
  if (pw.length >= 8)  score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { level: 1, label: 'Weak',   color: '#ef4444' };
  if (score <= 2) return { level: 2, label: 'Fair',   color: '#f97316' };
  if (score <= 3) return { level: 3, label: 'Good',   color: '#eab308' };
  return              { level: 4, label: 'Strong', color: '#22c55e' };
}

export default function PasswordSetupModal({ onClose, existingPassword, onPasswordChanged }) {
  const [step, setStep]         = useState(existingPassword ? S.MANAGE : S.ENTER);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const reset = () => { setPassword(''); setConfirm(''); setError(''); };

  // ── Remove ─────────────────────────────────────────────────────────────────
  const handleRemove = async () => {
    setLoading(true); setError('');
    try {
      await api.delete('/user/password');
      onPasswordChanged(false);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove password.');
    } finally { setLoading(false); }
  };

  // ── Enter → Confirm ───────────────────────────────────────────────────────
  const handleEnter = (e) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setError('');
    setConfirm('');
    setStep(S.CONFIRM);
  };

  // ── Confirm → Save ────────────────────────────────────────────────────────
  const handleConfirm = async (e) => {
    e.preventDefault();
    if (confirm !== password) {
      setError('Passwords do not match. Please start again.');
      reset(); setStep(S.ENTER); return;
    }
    setLoading(true); setError('');
    try {
      await api.post('/user/password', { password });
      onPasswordChanged(true);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save password. Try again.');
      reset(); setStep(S.ENTER);
    } finally { setLoading(false); }
  };

  const pw = strength(password);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-sm rounded-2xl p-6 animate-fade-in"
        style={{ backgroundColor: 'var(--lx-surface)', border: '1px solid var(--lx-border)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-400 text-lg transition-colors">✕</button>

        {/* ── MANAGE ── */}
        {step === S.MANAGE && (
          <>
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-indigo-950/60 border border-indigo-800/50 rounded-full
                flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">🔑</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Password Login</h3>
              <p className="text-gray-400 text-sm">You have a password set for sign-in.</p>
            </div>
            {error && <p className="text-red-400 text-sm text-center mb-4">{error}</p>}
            <div className="space-y-3">
              <button onClick={() => { reset(); setStep(S.ENTER); }} disabled={loading}
                className="w-full py-2.5 rounded-xl text-sm font-semibold btn-primary">
                Change Password
              </button>
              <button onClick={handleRemove} disabled={loading}
                className="w-full py-2.5 rounded-xl text-sm font-semibold transition-colors"
                style={{ backgroundColor: 'rgba(127,29,29,0.3)', border: '1px solid rgba(185,28,28,0.5)', color: '#fca5a5' }}>
                {loading ? 'Removing…' : 'Remove Password'}
              </button>
            </div>
          </>
        )}

        {/* ── ENTER new password ── */}
        {step === S.ENTER && (
          <form onSubmit={handleEnter} className="space-y-4">
            <div className="text-center mb-2">
              <h3 className="text-lg font-bold text-white mb-1">
                {existingPassword ? 'Change Password' : 'Set up Password Login'}
              </h3>
              <p className="text-gray-400 text-sm">Min 8 characters. Hashed with bcrypt, never stored plain.</p>
            </div>

            <PasswordInput
              label="New password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              placeholder="Enter new password"
              disabled={loading}
            />

            {/* Strength bar */}
            {password.length > 0 && (
              <div>
                <div className="flex gap-1 mb-1">
                  {[1,2,3,4].map((i) => (
                    <div key={i} className="h-1 flex-1 rounded-full transition-all"
                      style={{ backgroundColor: i <= pw.level ? pw.color : 'var(--lx-muted)' }} />
                  ))}
                </div>
                <p className="text-xs" style={{ color: pw.color }}>{pw.label}</p>
              </div>
            )}

            {error && <p className="text-red-400 text-xs">{error}</p>}

            <button type="submit" disabled={loading || password.length < 8} className="btn-primary w-full">
              Next →
            </button>

            {existingPassword && (
              <button type="button" onClick={() => { reset(); setStep(S.MANAGE); }}
                className="w-full text-sm text-gray-500 hover:text-gray-300 transition-colors">
                ← Back
              </button>
            )}
          </form>
        )}

        {/* ── CONFIRM ── */}
        {step === S.CONFIRM && (
          <form onSubmit={handleConfirm} className="space-y-4">
            <div className="text-center mb-2">
              <h3 className="text-lg font-bold text-white mb-1">Confirm Password</h3>
              <p className="text-gray-400 text-sm">Re-enter your password to confirm.</p>
            </div>

            <PasswordInput
              label="Confirm password"
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setError(''); }}
              placeholder="Re-enter password"
              disabled={loading}
            />

            {error && <p className="text-red-400 text-xs">{error}</p>}

            <button type="submit" disabled={loading || !confirm} className="btn-primary w-full">
              {loading
                ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</>
                : 'Confirm & Save Password'}
            </button>
            <button type="button" onClick={() => { reset(); setStep(S.ENTER); }}
              className="w-full text-sm text-gray-500 hover:text-gray-300 transition-colors">
              ← Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
